/**
 * Shared site-identity helpers for outbound notification email.
 *
 * Inbox subjects use `composeInboxSiteLabel` (store name + city/state).
 * Full street belongs in the LOCATION card / body list, not the subject.
 * Digest/body suffixes still rank street address above a bare name via
 * `assembleNotificationPlaceSuffix` — many sites store a person or
 * storefront label as `locations.name`, which is not enough to find the
 * property in a list.
 */

import {
  canonicalStateName,
  stateAbbreviationOrOriginal,
  US_STATE_NAMES,
} from "@/lib/virtualspace/state-names";

export type NotificationPlaceParts = {
  locationNumber?: string | null;
  locationName?: string | null;
  address?: string | null;
};

function normalizePart(value: string | null | undefined): string | null {
  const v = value?.trim();
  return v ? v : null;
}

/** Slugify for comparing location codes to addresses (import hygiene). */
function slugifyPlaceText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * True when `locations.code` is a slugified street address (common on
 * Airtable/NYU imports) rather than a short store/site number like `1496`.
 * Those slugs duplicate the address in every customer email subject/bullet.
 */
export function isAddressSlugLocationNumber(
  locationNumber: string | null | undefined,
  opts?: {
    address?: string | null;
    locationName?: string | null;
  },
): boolean {
  const code = normalizePart(locationNumber)?.toLowerCase() ?? null;
  if (!code) return false;

  const hyphenCount = (code.match(/-/g) ?? []).length;
  // Real codes: "1496", "CFA-12", "BK-1". Slugs: "115-55th-street-kings-…".
  if (hyphenCount < 2) return false;
  if (code.length < 20 && hyphenCount < 3) return false;

  const addrSlug = opts?.address ? slugifyPlaceText(opts.address) : "";
  const nameSlug = opts?.locationName
    ? slugifyPlaceText(opts.locationName)
    : "";
  if (addrSlug) {
    if (
      code === addrSlug ||
      code.startsWith(addrSlug) ||
      addrSlug.startsWith(code) ||
      (addrSlug.length >= 12 && code.includes(addrSlug.slice(0, 12)))
    ) {
      return true;
    }
  }
  if (nameSlug && nameSlug.length >= 8) {
    if (code === nameSlug || code.includes(nameSlug) || nameSlug.includes(code)) {
      return true;
    }
  }
  // Long multi-hyphen tokens without a short store-number shape.
  return hyphenCount >= 4 && code.length >= 24;
}

/**
 * Location # for customer-facing notification copy. Omits address-slug
 * codes so subjects/bullets/facts do not repeat the street as a "code".
 */
export function customerFacingLocationNumber(
  locationNumber: string | null | undefined,
  opts?: {
    address?: string | null;
    locationName?: string | null;
  },
): string | null {
  const code = normalizePart(locationNumber);
  if (!code) return null;
  if (isAddressSlugLocationNumber(code, opts)) return null;
  return code;
}

/**
 * Collapse USPS ↔ full state spelling and punctuation so
 * "Portland, ME 04101" and "Portland, Maine 04101" compare equal.
 */
export function normalizePlaceCompareKey(
  value: string | null | undefined,
): string {
  const raw = value ?? "";
  const cached = placeCompareKeyCache.get(raw);
  if (cached !== undefined) return cached;
  const key = computePlaceCompareKey(raw);
  // Bounded: a long-lived server instance sees an open-ended set of sites.
  if (placeCompareKeyCache.size >= PLACE_COMPARE_KEY_CACHE_LIMIT) {
    placeCompareKeyCache.clear();
  }
  placeCompareKeyCache.set(raw, key);
  return key;
}

/**
 * Production Lab classify runs the where label (and so this key) for every
 * corpus row, and wide views repeat the same site strings across ~25k rows
 * (Lab speed x-ray, #4788 Ticket 3). The key is a pure function of its input,
 * so a hit returns exactly what a fresh computation would.
 */
const PLACE_COMPARE_KEY_CACHE_LIMIT = 10_000;
const placeCompareKeyCache = new Map<string, string>();

/** Uncached computations so far; tests read it to prove the cache holds. */
let placeCompareKeyComputations = 0;
export function placeCompareKeyComputationCount(): number {
  return placeCompareKeyComputations;
}

type StateNameRule = {
  /** Literal text the pattern needs, or null when the name holds a regex metacharacter. */
  needle: string | null;
  pattern: RegExp;
  abbr: string;
};
let stateNameRules: StateNameRule[] | null = null;

/**
 * The state-name rewrites, built once (they used to be rebuilt, sorted and
 * recompiled on every call). Longest full names first so "West Virginia"
 * wins over "Virginia". Same order, same patterns, same replacements.
 */
function placeCompareStateNameRules(): StateNameRule[] {
  if (stateNameRules) return stateNameRules;
  const fullNames = Object.values(US_STATE_NAMES).sort(
    (a, b) => b.length - a.length,
  );
  const rules: StateNameRule[] = [];
  for (const full of fullNames) {
    const abbr = stateAbbreviationOrOriginal(full);
    if (!abbr || abbr.length !== 2) continue;
    const name = full.toLowerCase();
    rules.push({
      // "Washington, D.C." keeps its unescaped `.` (any character), so only a
      // plain name may skip the regex on a substring miss.
      needle: /^[a-z ]+$/.test(name) ? name : null,
      pattern: new RegExp(`\\b${name}\\b`, "g"),
      abbr: abbr.toLowerCase(),
    });
  }
  stateNameRules = rules;
  return rules;
}

function computePlaceCompareKey(value: string): string {
  placeCompareKeyComputations++;
  let s = value.trim().toLowerCase();
  if (!s) return "";
  s = s.replace(/[.,/#]/g, " ").replace(/\s+/g, " ").trim();
  for (const rule of placeCompareStateNameRules()) {
    // A pattern only matches where its name occurs in the current string, so
    // the substring check skips the regex pass for the other ~50 states.
    if (rule.needle === null || s.includes(rule.needle)) {
      s = s.replace(rule.pattern, rule.abbr);
    }
  }
  // Canonicalize any leftover 2-letter codes that match a US state.
  s = s
    .split(" ")
    .map((token) => {
      if (token.length !== 2) return token;
      const full = canonicalStateName(token.toUpperCase());
      const abbr = stateAbbreviationOrOriginal(full);
      return abbr && abbr.length === 2 ? abbr.toLowerCase() : token;
    })
    .join(" ");
  return s.replace(/\s+/g, " ").trim();
}

/** True when two place strings are the same site after state normalization. */
export function notificationPlaceTextsOverlap(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = normalizePlaceCompareKey(a);
  const right = normalizePlaceCompareKey(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
}

/**
 * True when `candidate` says nothing `context` does not already say — its
 * normalized key is *contained in* the context's.
 *
 * Directional on purpose. `notificationPlaceTextsOverlap` matches containment
 * both ways, so using it to suppress a location name also deletes a name that
 * merely embeds the street: "Chick-fil-A 525 Delsea Drive North" beside
 * "525 Delsea Drive North, Glassboro, NJ" would lose the brand, leaving a title
 * strictly less identifying than before. A candidate that strictly contains the
 * context is the richer string and is kept.
 */
export function placeTextIsRedundantWith(
  candidate: string | null | undefined,
  context: string | null | undefined,
): boolean {
  const left = normalizePlaceCompareKey(candidate);
  const right = normalizePlaceCompareKey(context);
  if (!left || !right) return false;
  return right.includes(left);
}

function alreadyCovered(parts: string[], candidate: string): boolean {
  return parts.some((p) => notificationPlaceTextsOverlap(p, candidate));
}

/** Prefer the longer / more specific of two overlapping place strings. */
export function preferRicherPlaceText(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  const left = normalizePart(a);
  const right = normalizePart(b);
  if (!left) return right;
  if (!right) return left;
  if (!notificationPlaceTextsOverlap(left, right)) {
    return `${left} — ${right}`;
  }
  return left.length >= right.length ? left : right;
}

/**
 * GIS dumps often store city as "GEORGE WEST". Title-case ALL-CAPS
 * tokens; leave mixed-case names (McKinney, St. Louis) alone.
 */
export function formatInboxCityName(
  city: string | null | undefined,
): string | null {
  const raw = normalizePart(city);
  if (!raw) return null;
  if (raw !== raw.toUpperCase() || !/[A-Z]/.test(raw)) return raw;
  return raw
    .toLowerCase()
    .split(/(\s+|-)/)
    .map((token) => {
      if (!token || token === "-" || /^\s+$/.test(token)) return token;
      return token.charAt(0).toUpperCase() + token.slice(1);
    })
    .join("");
}

/** Compact locality for inbox subjects: "George West, TX". */
export function formatInboxLocality(
  city?: string | null,
  state?: string | null,
): string | null {
  const cityName = formatInboxCityName(city);
  const st = stateAbbreviationOrOriginal(state);
  if (cityName && st) return `${cityName}, ${st}`;
  return cityName || st;
}

/**
 * Short site identity for email subjects: store name + city/state.
 * Full street stays in the location card, not the inbox title.
 */
export function composeInboxSiteLabel(
  args: NotificationPlaceParts & {
    city?: string | null;
    state?: string | null;
  },
): string | null {
  const locality = formatInboxLocality(args.city, args.state);
  const name = normalizePart(args.locationName);
  const number = customerFacingLocationNumber(args.locationNumber, {
    address: args.address,
    locationName: args.locationName,
  });
  const street = firstStreetLine(args.address);
  const head =
    (name && !placeTextIsRedundantWith(name, locality) ? name : null) ||
    number ||
    street ||
    null;

  const parts: string[] = [];
  const push = (value: string | null | undefined) => {
    const v = normalizePart(value);
    if (!v) return;
    if (alreadyCovered(parts, v)) return;
    parts.push(v);
  };
  push(head);
  push(locality);
  if (parts.length === 0) return normalizePart(args.address);
  return parts.join(" · ");
}

function firstStreetLine(address: string | null | undefined): string | null {
  const raw = normalizePart(address);
  if (!raw) return null;
  const street = raw.split(",")[0]?.trim() || null;
  return street && street.length >= 3 ? street : raw;
}

/**
 * One customer/program label for subjects. Drops "Acme · Acme - As Builts".
 */
export function composeDistinctOrgProgramLabel(
  customerName?: string | null,
  programName?: string | null,
): string | null {
  const customer = normalizePart(customerName);
  const program = normalizePart(programName);
  if (!customer) return program;
  if (!program) return customer;
  if (notificationPlaceTextsOverlap(customer, program)) {
    return customer.length >= program.length ? customer : program;
  }
  if (placeTextIsRedundantWith(customer, program)) return program;
  if (placeTextIsRedundantWith(program, customer)) return customer;
  return `${customer} · ${program}`;
}

/**
 * Operator "New request" inbox subject. Site first, then customer/program
 * when they add identity the site does not already say.
 */
export function composeNewRequestEmailSubject(args: {
  customerName?: string | null;
  programName?: string | null;
  siteLabel?: string | null;
  scopeBit?: string | null;
  orderShortId?: string | null;
}): string {
  const who = composeDistinctOrgProgramLabel(
    args.customerName,
    args.programName,
  );
  const site = normalizePart(args.siteLabel);
  const scope = normalizePart(args.scopeBit);
  const parts: string[] = [];
  const push = (value: string | null) => {
    if (!value) return;
    if (alreadyCovered(parts, value)) return;
    parts.push(value);
  };
  push(scope);
  push(site);
  if (
    who &&
    !parts.some(
      (part) =>
        notificationPlaceTextsOverlap(part, who) ||
        placeTextIsRedundantWith(who, part),
    )
  ) {
    parts.push(who);
  }
  if (parts.length === 0) {
    const shortId = normalizePart(args.orderShortId);
    return shortId ? `New request #${shortId}` : "New request received";
  }
  return `New request — ${parts.join(" · ")}`;
}

/**
 * Operator "Proposal accepted" inbox subject. Proposal number first when
 * present so the thread is searchable, then site / customer.
 */
export function composeProposalAcceptedEmailSubject(args: {
  customerName?: string | null;
  programName?: string | null;
  siteLabel?: string | null;
  scopeBit?: string | null;
  proposalNumber?: string | null;
  signerName?: string | null;
}): string {
  const who = composeDistinctOrgProgramLabel(
    args.customerName,
    args.programName,
  );
  const site = normalizePart(args.siteLabel);
  const scope = normalizePart(args.scopeBit);
  const proposal = normalizePart(args.proposalNumber);
  const signer = normalizePart(args.signerName);
  const parts: string[] = [];
  const push = (value: string | null) => {
    if (!value) return;
    if (alreadyCovered(parts, value)) return;
    parts.push(value);
  };
  push(proposal);
  push(scope);
  push(site);
  if (
    who &&
    !parts.some(
      (part) =>
        notificationPlaceTextsOverlap(part, who) ||
        placeTextIsRedundantWith(who, part),
    )
  ) {
    parts.push(who);
  }
  if (parts.length === 0) {
    return signer ? `Proposal accepted — signed by ${signer}` : "Proposal accepted";
  }
  return `Proposal accepted — ${parts.join(" · ")}`;
}

/**
 * Operator "Proposal expiring / expired" inbox subject. Same place order as
 * accepted so the thread stays searchable by proposal number.
 */
export function composeProposalExpiringEmailSubject(args: {
  expired: boolean;
  customerName?: string | null;
  programName?: string | null;
  siteLabel?: string | null;
  scopeBit?: string | null;
  proposalNumber?: string | null;
}): string {
  const who = composeDistinctOrgProgramLabel(
    args.customerName,
    args.programName,
  );
  const site = normalizePart(args.siteLabel);
  const scope = normalizePart(args.scopeBit);
  const proposal = normalizePart(args.proposalNumber);
  const parts: string[] = [];
  const push = (value: string | null) => {
    if (!value) return;
    if (alreadyCovered(parts, value)) return;
    parts.push(value);
  };
  push(proposal);
  push(scope);
  push(site);
  if (
    who &&
    !parts.some(
      (part) =>
        notificationPlaceTextsOverlap(part, who) ||
        placeTextIsRedundantWith(who, part),
    )
  ) {
    parts.push(who);
  }
  const lead = args.expired ? "Proposal expired" : "Proposal expiring";
  if (parts.length === 0) return lead;
  return `${lead} — ${parts.join(" · ")}`;
}

/**
 * Operator "Proposal declined" inbox subject.
 */
export function composeProposalDeclinedEmailSubject(args: {
  customerName?: string | null;
  programName?: string | null;
  siteLabel?: string | null;
  scopeBit?: string | null;
  proposalNumber?: string | null;
}): string {
  const who = composeDistinctOrgProgramLabel(
    args.customerName,
    args.programName,
  );
  const site = normalizePart(args.siteLabel);
  const scope = normalizePart(args.scopeBit);
  const proposal = normalizePart(args.proposalNumber);
  const parts: string[] = [];
  const push = (value: string | null) => {
    if (!value) return;
    if (alreadyCovered(parts, value)) return;
    parts.push(value);
  };
  push(proposal);
  push(scope);
  push(site);
  if (
    who &&
    !parts.some(
      (part) =>
        notificationPlaceTextsOverlap(part, who) ||
        placeTextIsRedundantWith(who, part),
    )
  ) {
    parts.push(who);
  }
  if (parts.length === 0) return "Proposal declined";
  return `Proposal declined — ${parts.join(" · ")}`;
}

/**
 * Subject/body place suffix from discrete parts (omit empties, dedupe).
 * Order: location # · address · name — address before name by design.
 * Prefer a composed scan name when it already encodes the site.
 */
export function assembleNotificationPlaceSuffix(
  args: NotificationPlaceParts & { scanName?: string | null },
): string | null {
  const scan = normalizePart(args.scanName);
  if (scan) return scan;

  const parts: string[] = [];
  const pushUnique = (value: string | null | undefined) => {
    const v = normalizePart(value);
    if (!v) return;
    if (alreadyCovered(parts, v)) return;
    parts.push(v);
  };

  pushUnique(
    customerFacingLocationNumber(args.locationNumber, {
      address: args.address,
      locationName: args.locationName,
    }),
  );
  pushUnique(args.address);
  pushUnique(args.locationName);
  if (parts.length === 0) return null;
  return parts.join(" · ");
}

/** Digest/list place label with Location TBD fallback. */
export function notificationPlaceLabel(
  args: NotificationPlaceParts & { fallback?: string },
): string {
  return (
    assembleNotificationPlaceSuffix(args) ??
    normalizePart(args.fallback) ??
    "Location TBD"
  );
}

/** One digest/list line: place — when (omit empties). */
export function formatNotificationMissionSummaryLine(args: {
  locationNumber?: string | null;
  locationName?: string | null;
  address?: string | null;
  whenLabel?: string | null;
  fallbackPlace?: string;
}): { place: string; when: string | null; line: string } {
  const place = notificationPlaceLabel({
    locationNumber: args.locationNumber,
    locationName: args.locationName,
    address: args.address,
    fallback: args.fallbackPlace,
  });
  const when = normalizePart(args.whenLabel);
  return {
    place,
    when,
    line: when ? `${place} — ${when}` : place,
  };
}
